import React, { useState, useEffect } from 'react';
import './VoicemailRetrySettings.css';


const VoicemailRetrySettings = ({
    formData = {},
    onChange,
    errors = {},
    disabled = false,
    /** When true, omit outer card chrome so a parent can supply `duplicate-data-rule-voicemail-shell`. */
    chromeless = false,
    /** When true, omit the short intro line (parent renders it beside the title). */
    hideDescription = false,
}) => {
    const [retryEnabled, setRetryEnabled] = useState(formData.voicemail_retry_enabled || false);

    useEffect(() => {
        setRetryEnabled(formData.voicemail_retry_enabled || false);
    }, [formData.voicemail_retry_enabled]);

    const handleRetryEnabledChange = (e) => {
        const checked = e.target.checked;
        setRetryEnabled(checked);
        onChange('voicemail_retry_enabled', checked);

        if (checked) {
            if (!formData.voicemail_retry_delay_days) {
                onChange('voicemail_retry_delay_days', 1);
            }
            if (!formData.voicemail_retry_max_attempts) {
                onChange('voicemail_retry_max_attempts', 1);
            }
        }
    };

    const handleFieldChange = (field, value) => {
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue)) {
            onChange(field, numValue);
        }
    };

    const fieldsContent = (
        <>
            <div className="row voicemail-retry-fields-row">
                <div className="col-md-6">
                    <div className="form-group">
                        <label className="voicemail-retry-field-label">
                            Callback delay (Days)
                        </label>

                        <input
                            type="number"
                            className={`form-control ${errors.voicemail_retry_delay_days ? 'is-invalid' : ''}`}
                            placeholder="e.g., 2"
                            value={formData.voicemail_retry_delay_days || ''}
                            onChange={(e) =>
                                handleFieldChange('voicemail_retry_delay_days', e.target.value)
                            }
                            min="1"
                            max="30"
                            disabled={disabled}
                        />

                        <div className="voicemail-retry-helper-text">
                            Must be between 1-30 days
                        </div>

                        {errors.voicemail_retry_delay_days && (
                            <div className="invalid-feedback d-block">
                                {errors.voicemail_retry_delay_days}
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="form-group">
                        <label className="voicemail-retry-field-label">
                            Maximum callback attempts <span className="voicemail-retry-required">*</span>
                        </label>

                        <input
                            type="number"
                            className={`form-control ${errors.voicemail_retry_max_attempts ? 'is-invalid' : ''}`}
                            placeholder="e.g., 3"
                            value={formData.voicemail_retry_max_attempts || ''}
                            onChange={(e) =>
                                handleFieldChange('voicemail_retry_max_attempts', e.target.value)
                            }
                            min="1"
                            max="10"
                            disabled={disabled}
                        />

                        <div className="voicemail-retry-helper-text">
                            Must be between 1-10 attempts
                        </div>

                        {errors.voicemail_retry_max_attempts && (
                            <div className="invalid-feedback d-block">
                                {errors.voicemail_retry_max_attempts}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {formData.send_type === 'now' && (
                <div className="form-group voicemail-retry-send-now-section">
                    <div className="voicemail-retry-send-now-wrapper">
                        <label className="voicemail-retry-send-now-label">
                            Apply to Send Now campaigns
                        </label>

                        <span
                            className={
                                formData.voicemail_retry_apply_to_send_now
                                    ? 'kt-switch kt-switch-sm kt-switch--success chat-cus-check voicemail-retry-switch'
                                    : 'kt-switch kt-switch-sm kt-switch--error chat-cus-check voicemail-retry-switch'
                            }
                        >
                            <label>
                                <input
                                    type="checkbox"
                                    checked={formData.voicemail_retry_apply_to_send_now || false}
                                    onChange={(e) =>
                                        onChange('voicemail_retry_apply_to_send_now', e.target.checked)
                                    }
                                    disabled={disabled}
                                />
                                <span />
                            </label>
                        </span>
                    </div>
                </div>
            )}
        </>
    );

    const toggleBlock = (
        <div className="form-group voicemail-retry-toggle">
            <div className="voicemail-retry-toggle-wrapper">
                <label className="voicemail-retry-toggle-label mb-0">
                    Enable Voicemail Retry
                </label>

                <span
                    className={
                        retryEnabled
                            ? 'kt-switch kt-switch-sm kt-switch--success chat-cus-check voicemail-retry-switch'
                            : 'kt-switch kt-switch-sm kt-switch--error chat-cus-check voicemail-retry-switch'
                    }
                >
                    <label>
                        <input
                            type="checkbox"
                            checked={retryEnabled}
                            onChange={handleRetryEnabledChange}
                            disabled={disabled}
                        />
                        <span />
                    </label>
                </span>
            </div>
        </div>
    );

    /* Campaign details card: toggle stays in header; fields expand to full row on enable */
    if (chromeless && hideDescription) {
        return (
            <div
                className={`voicemail-retry-settings voicemail-retry-settings--chromeless${
                    retryEnabled ? ' voicemail-retry-settings--expanded' : ''
                }`}
            >
                <div className="voicemail-retry-settings__toggle-row">
                    {toggleBlock}
                </div>
                {retryEnabled && (
                    <div className="voicemail-retry-settings__fields-panel">
                        <div className="voicemail-retry-settings-container">
                            {fieldsContent}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className={`voicemail-retry-settings${chromeless ? ' voicemail-retry-settings--chromeless' : ''}`}
        >
            <div className="row">
                <div className="col-md-12">
                    {toggleBlock}

                    {!hideDescription && (
                        <p className="rules-match-voicemail-desc">
                            Configure automatic callback attempts for voicemail responses.
                        </p>
                    )}

                    {retryEnabled && (
                        <div className="voicemail-retry-settings-container mt-3">
                            {fieldsContent}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoicemailRetrySettings;
